import { Link } from "react-router-dom";
import { Home, CheckCircle2, Shield, Umbrella, Package, ArrowRight, ArrowLeft, Star, Phone, DollarSign, Clock, Users } from "lucide-react";

const RentersPage = () => {
  const benefits = [
    {
      title: "Protect Your Belongings",
      description: "Coverage for furniture, electronics, clothing, and all your personal property.",
      icon: Package,
    },
    {
      title: "Affordable Rates",
      description: "Starting at just $5/month for comprehensive protection.",
      icon: DollarSign,
    },
    {
      title: "Liability Protection",
      description: "Coverage if someone is injured in your rental or you damage someone's property.",
      icon: Shield,
    },
    {
      title: "Peace of Mind",
      description: "Your landlord's insurance doesn't cover your stuff - but ours does.",
      icon: Users,
    },
  ];

  const coverageOptions = [
    { coverage: "$15,000", liability: "$100K", price: "$5", popular: false },
    { coverage: "$30,000", liability: "$100K", price: "$12", popular: true },
    { coverage: "$50,000", liability: "$300K", price: "$18", popular: false },
    { coverage: "$75,000", liability: "$500K", price: "$25", popular: false },
  ];

  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      {/* Header Banner - Blue */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              <Home className="w-6 h-6" />
              <div>
                <h1 className="font-bold text-lg">Renters Insurance</h1>
                <p className="text-blue-100 text-sm">Powered by My Insurance Coverage</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="bg-white border-b border-slate-200 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span>Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-blue-600" />
              <a href="tel:201-227-6413" className="hover:text-blue-600">Need help? Call 201.227.6413</a>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              <span>My Insurance Coverage - Licensed Agent</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section with Background Icon */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 relative overflow-hidden">
          {/* Background Icon */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <Home className="w-[500px] h-[500px] text-blue-900" strokeWidth={1} />
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 items-center relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Home className="w-4 h-4" />
                Renters Insurance
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
                Protect Your Belongings.
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-700">
                  Peace of Mind Included.
                </span>
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Your landlord's insurance doesn't cover your stuff. Get affordable renters insurance starting at just $5/month with coverage for everything you own.
              </p>
              <Link
                to="/quote?type=renters"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold rounded-full hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg shadow-blue-500/30"
                data-testid="renters-get-quote"
              >
                Get Your Free Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-sm text-slate-500 mt-3">Quick 2-minute application</p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-blue-200 rounded-3xl transform -rotate-2"></div>
              <div className="relative bg-white rounded-2xl shadow-xl p-6 border border-blue-100">
                <h3 className="text-lg font-bold text-navy-900 mb-4">Quick Coverage Estimate</h3>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 mb-4">
                  <p className="text-sm text-blue-700 mb-1">Recommended Coverage</p>
                  <p className="text-3xl font-bold text-navy-900">$30,000</p>
                  <p className="text-sm text-slate-500 mt-1">Personal property protection</p>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <p className="text-sm text-slate-500">Starting at</p>
                    <p className="text-2xl font-bold text-navy-900">$5/mo</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl text-center">
                    <p className="text-sm text-slate-500">Liability up to</p>
                    <p className="text-2xl font-bold text-navy-900">$300K</p>
                  </div>
                </div>
                <Link
                  to="/quote?type=renters"
                  className="block w-full py-3 bg-blue-600 text-white text-center font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                >
                  See Your Rate →
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-navy-900 mb-6 text-center">Why Renters Insurance?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-semibold text-navy-900 mb-2">{benefit.title}</h4>
                <p className="text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Coverage Options */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-navy-900 mb-2 text-center">Sample Rates</h3>
          <p className="text-slate-600 text-center mb-8">Coverage options for a typical apartment renter</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coverageOptions.map((option, index) => (
              <div
                key={index}
                className={`relative p-6 rounded-2xl border-2 transition-all ${
                  option.popular
                    ? "border-blue-400 bg-blue-50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                {option.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                <p className="text-2xl font-bold text-navy-900 mb-1">{option.coverage}</p>
                <p className="text-slate-500 text-sm mb-1">Personal property</p>
                <p className="text-slate-500 text-sm mb-3">{option.liability} liability</p>
                <p className="text-xl font-bold text-blue-600 mb-4">
                  {option.price}<span className="text-sm font-normal text-slate-500">/mo</span>
                </p>
                <Link
                  to="/quote?type=renters"
                  className={`block w-full py-2 text-center font-semibold rounded-lg transition-colors ${
                    option.popular
                      ? "bg-blue-500 text-white hover:bg-blue-600"
                      : "bg-slate-100 text-navy-700 hover:bg-slate-200"
                  }`}
                >
                  Get Quote
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-slate-400 mt-6">
            * Actual rates may vary based on location, coverage amounts, and other factors.
          </p>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Ready to Protect Your Belongings?</h3>
          <p className="text-blue-100 mb-6 max-w-xl mx-auto">
            Get a free quote in just 2 minutes. Most policies can start the same day.
          </p>
          <Link
            to="/quote?type=renters"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-700 font-bold rounded-full hover:bg-slate-100 transition-colors"
          >
            Get Your Free Quote
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Contact Card */}
        <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Phone className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-navy-900">Prefer to Speak with Bradley?</h4>
                <p className="text-sm text-slate-600">Your licensed insurance expert is ready to help</p>
              </div>
            </div>
            <a
              href="tel:201-227-6413"
              className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition-colors"
            >
              Call 201.227.6413
            </a>
          </div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="bg-slate-100 border-t border-slate-200 py-4 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-500">
            Renters insurance quotes provided through My Insurance Coverage, licensed insurance agent.
            <br />
            <span className="text-slate-400">
              Coverage underwritten by A-rated insurance carriers.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RentersPage;
